<template>
    <div class="call-player-root">
        <v-card
            v-if="currentCall && isOpen"
            class="call-player-widget"
            elevation="8"
        >
            <div class="call-player-marquee">
                <div
                    class="call-player-marquee-track"
                    :style="{ animationDuration: marqueeDuration }"
                >
                    {{ marqueeText }}
                </div>
            </div>

            <div class="call-player-body">
                <v-btn
                    icon
                    large
                    color="primary"
                    class="call-player-toggle"
                    @click="togglePlayPause"
                >
                    <v-icon size="40">{{ isPlaying ? 'mdi-pause-circle' : 'mdi-play-circle' }}</v-icon>
                </v-btn>

                <div class="call-player-controls">
                    <v-slider
                        v-model="sliderValue"
                        :max="duration"
                        hide-details
                        dense
                        @start="onSeekStart"
                        @end="onSeekEnd"
                    />
                    <div class="call-player-time d-flex justify-space-between">
                        <span>{{ formatTime(sliderValue) }}</span>
                        <span>{{ formatTime(duration) }}</span>
                    </div>
                </div>

                <v-btn-toggle
                    v-model="playbackRate"
                    mandatory
                    dense
                    color="primary"
                    class="call-player-speed"
                >
                    <v-btn :value="1" small>1x</v-btn>
                    <v-btn :value="2" small>2x</v-btn>
                </v-btn-toggle>

                <v-tooltip top>
                    <template v-slot:activator="{ on, attrs }">
                        <v-btn
                            v-bind="attrs"
                            v-on="on"
                            icon
                            small
                            class="call-player-download"
                            :disabled="!canDownload"
                            @click="downloadRecording"
                        >
                            <v-icon small>{{ isDownloading ? 'mdi-loading' : 'mdi-download' }}</v-icon>
                        </v-btn>
                    </template>
                    <span>Завантажити запис</span>
                </v-tooltip>

                <v-btn icon small class="call-player-close" @click="close">
                    <v-icon small>mdi-close</v-icon>
                </v-btn>
            </div>
        </v-card>

        <audio
            ref="audio"
            @loadedmetadata="onLoadedMetadata"
            @timeupdate="onTimeUpdate"
            @ended="onEnded"
            @play="onNativePlay"
            @pause="onNativePause"
            @error="onPlaybackError"
        />
    </div>
</template>

<script>
import {mapGetters} from 'vuex';

export default {
    name: 'CallRecordingPlayerWidget',
    data() {
        return {
            isPlaying: false,
            duration: 0,
            sliderValue: 0,
            isSeeking: false,
            loadedCallId: null,
            pendingSeek: 0,
            isDownloading: false,
            // Черга дзвінків, з якої натиснули play (поточна сторінка списку/звіту),
            // потрібна тільки плеєру для автопереходу — глобальному стану не належить.
            queue: [],
        };
    },
    computed: {
        ...mapGetters('CallPlayerModule', ['currentCall', 'isOpen']),
        playbackRate: {
            get() {
                return this.$store.getters['CallPlayerModule/playbackRate'];
            },
            set(value) {
                this.$store.dispatch('CallPlayerModule/setPlaybackRate', value);
            },
        },
        marqueeText() {
            if (!this.currentCall) {
                return '';
            }

            const manager = this.getManagerLabel(this.currentCall);
            const client = this.getClientLabel(this.currentCall);

            return this.currentCall.direction === 'in'
                ? `${client} → ${manager}`
                : `${manager} → ${client}`;
        },
        marqueeDuration() {
            const seconds = Math.max(6, this.marqueeText.length * 0.15);

            return `${seconds}s`;
        },
        canDownload() {
            return Boolean(
                this.currentCall
                && this.currentCall.record_link
                && this.getCallId(this.currentCall)
                && !this.isDownloading
            );
        },
    },
    watch: {
        playbackRate(value) {
            const audio = this.$refs.audio;

            if (audio) {
                audio.playbackRate = value;
            }
        },
    },
    mounted() {
        this.eventHub.$on('callRecordingPlay', this.handlePlayRequest);
    },
    beforeDestroy() {
        this.pendingSeek = 0;
        this.eventHub.$off('callRecordingPlay', this.handlePlayRequest);
    },
    methods: {
        handlePlayRequest({call, queue} = {}) {
            this.queue = queue || [];
            this.playCall(call);
        },
        // Синхронно, без await/nextTick перед play() — <audio> тепер завжди в DOM (без v-if),
        // а браузер дозволяє autoplay без взаємодії користувача лише якщо play() викликається
        // в тому ж синхронному стеку виклику, що й клік. Будь-яка await-пауза перед play()
        // розриває цей ланцюжок, і браузер тихо відхиляє відтворення (без помилки в консолі).
        // Автоперехід через onEnded() теж синхронний і викликає play() на тому самому <audio>
        // елементі, який уже отримав дозвіл на відтворення від початкового кліку користувача.
        playCall(call) {
            if (!call) {
                return;
            }

            this.$store.dispatch('CallPlayerModule/setCurrentCall', call);
            this.$store.dispatch('CallPlayerModule/setOpen', true);

            const audio = this.$refs.audio;

            if (!audio) {
                return;
            }

            const callId = this.getCallId(call);
            const start = Number(call.play_from || 0);
            this.pendingSeek = start;

            if (this.loadedCallId !== callId) {
                this.loadedCallId = callId;
                this.duration = call.duration_seconds || 0;
                this.sliderValue = start;

                audio.src = call.record_link;
                audio.currentTime = 0;
                audio.playbackRate = this.playbackRate;
                audio.play().catch(error => this.onPlaybackError(error));
                return;
            }

            if (call.play_from) {
                audio.currentTime = start;
                audio.play().catch(error => this.onPlaybackError(error));
                return;
            }

            if (audio.paused) {
                audio.play().catch(error => this.onPlaybackError(error));
            } else {
                audio.pause();
            }
        },
        playNextInQueue() {
            const currentIndex = this.queue.findIndex(item => this.getCallId(item) === this.loadedCallId);

            if (currentIndex === -1) {
                return;
            }

            const nextCall = this.queue
                .slice(currentIndex + 1)
                .find(item => item.record_link);

            if (nextCall) {
                this.playCall(nextCall);
            }
        },
        getCallId(call) {
            return call ? (call.id || call.callsapp_call_id) : null;
        },
        togglePlayPause() {
            const audio = this.$refs.audio;

            if (!audio) {
                return;
            }

            if (audio.paused) {
                audio.play().catch(error => this.onPlaybackError(error));
            } else {
                audio.pause();
            }
        },
        close() {
            const audio = this.$refs.audio;
            this.pendingSeek = 0;

            if (audio) {
                audio.pause();
            }

            this.$store.dispatch('CallPlayerModule/close');
        },
        async downloadRecording() {
            const call = this.currentCall;
            const callId = this.getCallId(call);

            if (!call || !call.record_link || !callId || this.isDownloading) {
                return;
            }

            this.isDownloading = true;

            try {
                const response = await this.axios.get(`callsapp/calls/${callId}/recording`, {
                    responseType: 'blob',
                });
                const contentType = (response.headers && response.headers['content-type']) || 'audio/mpeg';
                const blob = response.data instanceof Blob
                    ? response.data
                    : new Blob([response.data], {type: contentType});
                const url = URL.createObjectURL(blob);
                const filename = this.getFilenameFromDisposition(response.headers && response.headers['content-disposition'])
                    || this.buildDownloadFilename(call);

                this.triggerDownload(url, filename);
                window.setTimeout(() => URL.revokeObjectURL(url), 1000);
            } catch (error) {
                if (error && error.status === 422) {
                    this.eventHub.$emit('doShowSnackbarEvent', true, 'Не вдалося завантажити запис дзвінка', 'warning', 3000);
                }
            } finally {
                this.isDownloading = false;
            }
        },
        triggerDownload(href, filename) {
            const link = document.createElement('a');

            link.href = href;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        },
        getFilenameFromDisposition(header) {
            if (!header) {
                return '';
            }

            const utfMatch = header.match(/filename\*\s*=\s*UTF-8''([^;]+)/i);

            if (utfMatch) {
                try {
                    return decodeURIComponent(utfMatch[1].trim());
                } catch (error) {
                    return utfMatch[1].trim();
                }
            }

            const match = header.match(/filename\s*=\s*"?([^";]+)"?/i);

            return match ? match[1].trim() : '';
        },
        buildDownloadFilename(call) {
            const phone = call.direction === 'in' ? call.caller_number : call.destination_number;
            const safePhone = String(phone || '').replace(/[^\d+]/g, '');
            const datePart = this.formatFilenameDate(call.started_at || call.ended_at || call.created_at);
            const id = this.getCallId(call) || 'record';
            const extension = this.getRecordingExtension(call.record_link);

            return `dzvinok_${safePhone || id}${datePart}.${extension}`;
        },
        formatFilenameDate(value) {
            if (!value) {
                return '';
            }

            const date = new Date(value);

            if (Number.isNaN(date.getTime())) {
                return '';
            }

            const pad = number => String(number).padStart(2, '0');

            return `_${date.getFullYear()}${pad(date.getMonth() + 1)}${pad(date.getDate())}_${pad(date.getHours())}${pad(date.getMinutes())}`;
        },
        getRecordingExtension(url) {
            try {
                const pathname = new URL(url, window.location.origin).pathname;
                const match = pathname.match(/\.(mp3|wav|ogg|m4a|aac)$/i);

                if (match) {
                    return match[1].toLowerCase();
                }
            } catch (error) {
                // ignore invalid urls
            }

            return 'mp3';
        },
        onPlaybackError(error) {
            console.log(error);
            this.eventHub.$emit('doShowSnackbarEvent', true, 'Не вдалося відтворити запис дзвінка', 'warning', 3000);
        },
        onLoadedMetadata() {
            const audio = this.$refs.audio;

            this.duration = audio && Number.isFinite(audio.duration)
                ? audio.duration
                : ((this.currentCall && this.currentCall.duration_seconds) || 0);

            if (audio && this.pendingSeek) {
                audio.currentTime = this.pendingSeek;
                this.sliderValue = this.pendingSeek;
            }
            this.pendingSeek = 0;
        },
        onTimeUpdate() {
            if (this.isSeeking) {
                return;
            }

            const audio = this.$refs.audio;
            this.sliderValue = audio ? audio.currentTime : 0;
        },
        onSeekStart() {
            this.isSeeking = true;
        },
        onSeekEnd(value) {
            this.isSeeking = false;
            const audio = this.$refs.audio;

            if (audio) {
                audio.currentTime = value;
            }
        },
        onEnded() {
            this.sliderValue = 0;
            this.playNextInQueue();
        },
        onNativePlay() {
            this.isPlaying = true;
            this.$store.dispatch('CallPlayerModule/setPlaying', true);
        },
        onNativePause() {
            this.isPlaying = false;
            this.$store.dispatch('CallPlayerModule/setPlaying', false);
        },
        formatTime(value) {
            const totalSeconds = Number.isFinite(Number(value)) ? Math.floor(Number(value)) : 0;
            const minutes = Math.floor(totalSeconds / 60);
            const seconds = totalSeconds % 60;

            return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
        },
        getManagerLabel(call) {
            return call.user && call.user.name ? call.user.name : 'Менеджер не визначений';
        },
        getClientLabel(call) {
            if (call.client && call.client.fio) {
                return call.client.fio;
            }

            const phone = call.direction === 'in' ? call.caller_number : call.destination_number;

            return phone || 'Невідомий клієнт';
        },
    },
};
</script>

<style scoped>
.call-player-root {
    position: fixed;
    right: 16px;
    bottom: 16px;
    z-index: 500;
}

.call-player-widget {
    width: 360px;
    max-width: calc(100vw - 32px);
    padding: 10px 12px;
    border-radius: 8px !important;
}

@media (max-width: 600px) {
    .call-player-root {
        right: 0;
        left: 0;
        bottom: 0;
    }

    .call-player-widget {
        width: 100%;
        max-width: 100%;
        border-radius: 0 !important;
    }
}

.call-player-marquee {
    position: relative;
    overflow: hidden;
    height: 20px;
    margin-bottom: 4px;
    white-space: nowrap;
}

.call-player-marquee-track {
    position: absolute;
    left: 100%;
    font-size: 13px;
    color: #616161;
    animation-name: call-player-marquee-scroll;
    animation-timing-function: linear;
    animation-iteration-count: infinite;
}

@keyframes call-player-marquee-scroll {
    from {
        transform: translateX(0);
    }
    to {
        transform: translateX(-200%);
    }
}

.call-player-body {
    display: flex;
    align-items: center;
    gap: 4px;
}

.call-player-toggle {
    flex-shrink: 0;
}

.call-player-controls {
    flex: 1;
    min-width: 0;
}

.call-player-time {
    margin-top: -8px;
    font-family: monospace;
    font-size: 12px;
    color: #757575;
}

.call-player-speed {
    flex-shrink: 0;
}

.call-player-download,
.call-player-close {
    flex-shrink: 0;
}
</style>
